import Block from '@mui/icons-material/Block';
import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import Typography, { type TypographyProps } from '@mui/material/Typography';
import { Ellipsis } from '@openx/components-core/lib/Ellipsis/Ellipsis';
import { lalSegmentStatus } from '@openx/types/segments';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const StyledNameTypography = (props: TypographyProps) => (
  <Typography
    variant="body1Bold"
    component="span"
    color="textPrimary"
    data-test="segment-name"
    {...props}
    sx={[
      {
        lineHeight: '22px',
      },
      ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
    ]}
  />
);

type ExpandCollapseIconProps = { expanded: boolean };
const EpxandCollapseIcon = ({ expanded, ...svgProps }: SvgIconProps & ExpandCollapseIconProps) => (
  <ChevronRight
    data-expandicon="expandRow"
    color="primary"
    data-test="expand-icon"
    {...svgProps}
    sx={{
      transform: expanded ? 'rotate(270deg)' : 'rotate(90deg)',

      ...(svgProps?.sx ?? {}),
    }}
  />
);

export const NameCell = ({ data }) => {
  const { t } = useTranslation();
  const { expanded, name, status, segment_audiences: segmentAudiences = [] } = data;

  const hasAssociatedAudiences = !!segmentAudiences.length;
  let isLalProcessingStatus = false;
  if ('status' in data) {
    isLalProcessingStatus = status === lalSegmentStatus.PROCESSING;
  }

  const tooltipTitle = useMemo(() => {
    const tooltipParts: string[] = [];
    if (hasAssociatedAudiences) {
      tooltipParts.push(t('Cannot be archived because of associated Audience(s).'));
    }
    if (isLalProcessingStatus) {
      tooltipParts.push(t('Cannot be archived because of processing status.'));
    }
    return tooltipParts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < tooltipParts.length - 1 && <br />}
      </React.Fragment>
    ));
  }, [t, hasAssociatedAudiences, isLalProcessingStatus]);

  return (
    <Box
      data-test="name-cell"
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <EpxandCollapseIcon expanded={expanded} sx={{ marginLeft: 2, marginRight: 1 }} />
      <Box
        data-test={`archive-${hasAssociatedAudiences || isLalProcessingStatus ? 'forbidden' : 'allowed'}-icon`}
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {hasAssociatedAudiences || isLalProcessingStatus ? (
          <Tooltip title={tooltipTitle}>
            <Block color="error" fontSize="small" />
          </Tooltip>
        ) : (
          <Tooltip title={t('Ready to archive')}>
            <Check fontSize="small" color="success" />
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          paddingLeft: 1,
          paddingRight: 1,
        }}
      >
        <StyledNameTypography data-test="row-title">
          <Ellipsis tooltip width="286px">
            {name}
          </Ellipsis>
        </StyledNameTypography>
      </Box>
    </Box>
  );
};
